/**
 * Persists in-flight upload state (per-photo) so a page refresh during the
 * upload step doesn't force the user to re-pick photos.
 *
 * We store dataUrls + per-file uploadedUrls in sessionStorage. The original
 * `File` objects can't be persisted (their underlying disk handle is gone
 * after reload), but we can rebuild equivalent Files from the stored dataUrls
 * on retry.
 *
 * sessionStorage (not localStorage) is intentional: the staged state is only
 * relevant within the same tab session. Quotas vary by browser but are
 * typically 5MB+, comfortably above the ~200KB-per-photo we produce after
 * compression × MAX_IMAGES (8).
 */

import type { CleanError } from "@/components/ErrorState";

const KEY = "cp_upload_attempt_v1";

export type PersistedAttempt = {
  /** Compressed JPEG previews, also used to rebuild File objects on retry. */
  dataUrls: string[];
  /** Same length as dataUrls. `null` means not yet uploaded. */
  uploadedUrls: (string | null)[];
  /** Original filenames so the error label and uploaded file keep their names. */
  filenames: string[];
  /** Last error so the UI can re-render the error state after refresh. */
  error: CleanError | null;
  retryCount: number;
  savedAt: number;
};

export const savePersistedAttempt = (a: PersistedAttempt): void => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(a));
  } catch (err) {
    // Quota exceeded or storage disabled — non-fatal, just skip persistence.
    console.warn("Couldn't persist upload state", err);
  }
};

export const loadPersistedAttempt = (): PersistedAttempt | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedAttempt;
    // Drop anything older than 5 minutes. Longer TTLs caused day-old error UIs
    // to resurrect on home-screen mount, which felt like a fresh failure to
    // the user. 5 min still survives "phone locked, came back" gaps.
    if (Date.now() - parsed.savedAt > 5 * 60 * 1000) {
      clearPersistedAttempt();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const clearPersistedAttempt = (): void => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
};

/**
 * Rebuild a JPEG File from a stored data URL. Used when retrying after a
 * refresh — the original File reference is gone but the bytes are in storage.
 */
export const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const [header, base64] = dataUrl.split(",", 2);
  const mimeMatch = /data:([^;]+)/.exec(header);
  const mime = mimeMatch?.[1] || "image/jpeg";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
};
